// src/components/commercials/CommercialDetail.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Tabs,
  Tab,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Link,
  useTheme,
  useMediaQuery,
  ListItemButton,
} from "@mui/material";
import {
  ArrowBack,
  Email,
  Phone,
  LocationOn,
  Business,
  Person,
  Receipt,
  Assignment,
  Visibility,
  Euro,
  CalendarToday,
  Badge,
  Percent,
  AttachFile,
  Description,
  OpenInNew,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import { PROJECT_STATUS_LABELS } from "../../utils/models/projectModel";

const CommercialDetail = ({ commercialId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { document: commercial, loading } = useDocument("commercials", commercialId);
  const { data: invoices } = useCollection("invoices");
  const { data: projects } = useCollection("projects");
  const [activeTab, setActiveTab] = useState(0);

  if (loading) return <LoadingSpinner />;
  if (!commercial) return <EmptyState title="Comercial no encontrado" />;

  const displayName = `${commercial.firstName || ""} ${commercial.lastName || ""}`.trim();

  const hasCompanyAddress = commercial.companyAddress?.street || commercial.companyAddress?.city;

  // Filtrar facturas y proyectos donde este comercial aparece como canal de entrada
  const commercialInvoices = invoices?.filter((i) => 
    i.entryChannelContactId === commercialId && i.isActive !== false
  ) || [];
  
  const commercialProjects = projects?.filter((p) => 
    p.entryChannelContactId === commercialId && p.isActive !== false
  ) || [];

  const sortedInvoices = [...commercialInvoices].sort((a, b) => 
    new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0)
  );

  const sortedProjects = [...commercialProjects].sort((a, b) => 
    new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "—";
    return `${Number(amount).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
  };

  const getInvoiceStatusLabel = (status) => {
    const labels = {
      draft: "Borrador",
      sent: "Enviada",
      paid: "Pagada",
      overdue: "Vencida",
      cancelled: "Cancelada",
    };
    return labels[status] || status || "—";
  };

  const getInvoiceStatusColor = (status) => {
    const colors = {
      draft: "default",
      sent: "info",
      paid: "success",
      overdue: "error",
      cancelled: "warning",
    };
    return colors[status] || "default";
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.12)"
            : "rgba(0, 95, 101, 0.12)",
        background: theme.palette.background.paper,
        overflow: "hidden",
      }}
    >
      {/* CABECERA */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 2 },
          mb: { xs: 3, sm: 4 },
          flexWrap: "wrap",
        }}
      >
        <IconButton
          onClick={() => navigate("/commercials")}
          size="small"
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
            mt: 0.5,
            "&:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "rgba(0, 175, 163, 0.1)"
                  : "rgba(0, 95, 101, 0.05)",
            },
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              wordBreak: "break-word",
            }}
          >
            {displayName || "Sin nombre"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Chip
              icon={<Person sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              label="Comercial"
              size="small"
              color="info"
              variant="outlined"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
            {commercial.companyName && (
              <Chip
                icon={<Business sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                label={commercial.companyName}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: theme.palette.info.main,
                  color: theme.palette.info.main,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            )}
            {commercial.percentage && (
              <Chip
                icon={<Percent sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                label={commercial.percentage}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: theme.palette.success.main,
                  color: theme.palette.success.main,
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          width: "100%",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* INFORMACIÓN - LADO IZQUIERDO */}
        <Box
          sx={{
            flex: { md: "1 1 340px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "300px" },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
              height: "100%",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.info.main,
                  borderBottom: "2px solid",
                  borderColor: theme.palette.info.main,
                  pb: 1,
                  display: "inline-block",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Datos personales
              </Typography>

              <List dense disablePadding>
                {commercial.email && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Email
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.info.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Email"
                      secondary={commercial.email}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                        wordBreak: "break-all",
                      }}
                    />
                  </ListItem>
                )}

                {commercial.phone && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Phone
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Teléfono"
                      secondary={commercial.phone}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {commercial.dni && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Badge
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.warning.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="DNI / NIE"
                      secondary={commercial.dni}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}

                {commercial.percentage && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Percent
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Porcentaje"
                      secondary={commercial.percentage}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
              </List>

              {/* DATOS DE LA EMPRESA */}
              {commercial.companyName && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      mb: 2,
                      color: theme.palette.info.main,
                      borderBottom: "2px solid",
                      borderColor: theme.palette.info.main,
                      pb: 1,
                      display: "inline-block",
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    Empresa
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Business
                          sx={{
                            fontSize: { xs: 18, sm: 20 },
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Nombre"
                        secondary={commercial.companyName}
                        primaryTypographyProps={{
                          variant: "caption",
                          color: "text.secondary",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          fontSize: { xs: "0.8rem", sm: "0.85rem" },
                        }}
                      />
                    </ListItem>
                    {commercial.companyTaxId && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Badge
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.warning.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="NIF / CIF"
                          secondary={commercial.companyTaxId}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                          }}
                        />
                      </ListItem>
                    )}
                    {hasCompanyAddress && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <LocationOn
                            sx={{
                              fontSize: { xs: 18, sm: 20 },
                              color: theme.palette.error.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Dirección"
                          secondary={`${commercial.companyAddress.street || ""}, ${commercial.companyAddress.city || ""}, ${commercial.companyAddress.state || ""} ${commercial.companyAddress.zipCode || ""}`}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </>
              )}

              {/* ARCHIVOS */}
              {commercial.files && commercial.files.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ 
                      mb: 2,
                      color: theme.palette.info.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    }}
                  >
                    <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />
                    Archivos adjuntos
                  </Typography>
                  <List dense disablePadding>
                    {commercial.files.map((file, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Description sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.info.main }} />
                        </ListItemIcon>
                        <ListItemText>
                          <Link
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{
                              color: theme.palette.info.main,
                              fontWeight: 500,
                              fontSize: { xs: "0.8rem", sm: "0.85rem" },
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              wordBreak: 'break-all',
                              '&:hover': {
                                color: theme.palette.info.dark,
                              },
                            }}
                          >
                            {file.name || file.url}
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </ListItemText>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* ENLACES A ENTIDADES RELACIONADAS */}
              {commercial.companyId && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <List dense disablePadding>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Business sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.primary.main }} />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          component="button"
                          onClick={() => navigate(`/companies/${commercial.companyId}`)}
                          underline="hover"
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                            cursor: "pointer",
                          }}
                        >
                          Ver empresa →
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </>
              )}

              {commercial.notes && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.palette.divider }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                  >
                    Notas
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {commercial.notes}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* FACTURAS Y PROYECTOS - LADO DERECHO */}
        <Box
          sx={{
            flex: { md: "2 1 540px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "350px" },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              background: theme.palette.background.paper,
              overflow: "hidden",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                px: { xs: 1, sm: 2 },
                pt: 2,
                borderBottom: "1px solid",
                borderColor: theme.palette.divider,
                "& .MuiTab-root": {
                  color: theme.palette.text.secondary,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  minWidth: { xs: "auto", sm: 90 },
                  px: { xs: 1, sm: 2 },
                  "&.Mui-selected": {
                    color: theme.palette.info.main,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.info.main,
                },
              }}
            >
              <Tab
                label={`Facturas (${commercialInvoices.length})`}
                icon={<Receipt sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                iconPosition="start"
              />
              <Tab
                label={`Proyectos (${commercialProjects.length})`}
                icon={<Assignment sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                iconPosition="start"
              />
            </Tabs>

            {/* LISTA DE FACTURAS */}
            {activeTab === 0 &&
              (sortedInvoices.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <EmptyState
                    title="Sin facturas"
                    description="Este comercial no tiene facturas asignadas aún"
                    compact
                  />
                </Box>
              ) : (
                <List dense disablePadding>
                  {sortedInvoices.map((invoice) => (
                    <ListItem
                      key={invoice.id}
                      disablePadding
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: theme.palette.divider,
                      }}
                    >
                      <ListItemButton
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1.5, sm: 2 },
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : "rgba(0, 95, 101, 0.03)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                          <Receipt
                            sx={{
                              fontSize: { xs: 20, sm: 24 },
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                            >
                              {invoice.invoiceNumber ||
                                `Factura #${invoice.id?.substring(0, 8)}`}
                            </Typography>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CalendarToday sx={{ fontSize: 12 }} />
                                {formatDate(invoice.date || invoice.createdAt)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Euro sx={{ fontSize: 12 }} />
                                {formatCurrency(invoice.total)}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={getInvoiceStatusLabel(invoice.status)}
                            size="small"
                            color={getInvoiceStatusColor(invoice.status)}
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              height: { xs: 22, sm: 24 },
                            }}
                          />
                          <Visibility
                            sx={{
                              fontSize: { xs: 16, sm: 20 },
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ))}

            {/* LISTA DE PROYECTOS */}
            {activeTab === 1 &&
              (sortedProjects.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <EmptyState
                    title="Sin proyectos"
                    description="Este comercial no tiene proyectos asignados aún"
                    compact
                  />
                </Box>
              ) : (
                <List dense disablePadding>
                  {sortedProjects.map((project) => (
                    <ListItem
                      key={project.id}
                      disablePadding
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: theme.palette.divider,
                      }}
                    >
                      <ListItemButton
                        onClick={() => navigate(`/projects/${project.id}`)}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1.5, sm: 2 },
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : "rgba(0, 95, 101, 0.03)",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: { xs: 36, sm: 40 } }}>
                          <Assignment
                            sx={{
                              fontSize: { xs: 20, sm: 24 },
                              color: theme.palette.secondary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                            >
                              {project.name || "Sin nombre"}
                            </Typography>
                          }
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                }}
                              >
                                {project.address?.street
                                  ? `${project.address.street}, ${project.address.city || ""}`
                                  : "Sin dirección"}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={
                              PROJECT_STATUS_LABELS[project.status] ||
                              project.status ||
                              "—"
                            }
                            size="small"
                            color={
                              project.status === "completed"
                                ? "success"
                                : project.status === "in_progress"
                                  ? "primary"
                                  : "default"
                            }
                            variant="outlined"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              height: { xs: 22, sm: 24 },
                            }}
                          />
                          <Visibility
                            sx={{
                              fontSize: { xs: 16, sm: 20 },
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ))}
          </Card>
        </Box>
      </Box>
    </Paper>
  );
};

export default CommercialDetail;